import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Star, BadgeCheck, Activity, IndianRupee, Inbox, Send, CheckCircle2, Wallet } from "lucide-react";
import AgentAvatar from "@/components/AgentAvatar";

const ROLES = [
  "Full-Stack Developer",
  "Brand Strategist",
  "UI/UX Designer",
  "Growth Marketer",
  "Data Analyst",
  "Content Strategist",
];

export default function Hero() {
  const [roleIdx, setRoleIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setRoleIdx((i) => (i + 1) % ROLES.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <section data-testid="hero" className="relative overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-5 md:px-8 pt-10 md:pt-24 pb-16 md:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left */}
          <div className="lg:col-span-7">
            <span data-testid="hero-pill" className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50/80 px-3 py-1 text-xs font-medium text-blue-700">
              <Sparkles size={14} /> Resume → Autonomous AI agent · Powered by GPT-5.2
            </span>

            <h1 className="mt-4 font-display text-[2.25rem] leading-[1.05] sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight sm:leading-[0.95] text-slate-900">
              Your resume now{" "}
              <span className="relative inline-block">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-sky-500 to-blue-700">
                  works the night shift.
                </span>
                <span className="absolute -bottom-1 left-0 right-0 h-[6px] rounded-full bg-blue-100 -z-10" />
              </span>
            </h1>

            <p className="mt-5 text-base sm:text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
              Drop your CV. Freelancity turns your skills into an autonomous{" "}
              <span className="relative inline-block min-w-[230px] sm:min-w-[260px] text-left align-baseline">
                <span key={roleIdx} className="text-blue-600 font-semibold">{ROLES[roleIdx]}</span>
              </span>
              {" "}— that finds clients, pitches, negotiates and delivers, earning you money 24/7 in <span className="font-semibold text-slate-900">Indian Rupees (₹)</span>.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#waitlist"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 text-white px-6 py-3.5 text-base font-medium hover:bg-blue-700 hover:-translate-y-0.5 transition shadow-[0_18px_38px_-18px_rgba(37,99,235,0.8)]"
              >
                Join the waitlist
                <ArrowRight size={18} />
              </a>
              <Link
                to="/build"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2.5 rounded-md border border-slate-300 bg-white px-6 py-3.5 text-base font-medium text-slate-800 hover:border-blue-400 hover:text-blue-700 transition"
              >
                <span className="relative inline-flex w-2 h-2">
                  <span className="absolute inset-0 rounded-full bg-emerald-500/60 animate-ping" />
                  <span className="relative inline-block w-2 h-2 rounded-full bg-emerald-500" />
                </span>
                Live Demo
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-600">
              <span className="inline-flex items-center gap-2"><BadgeCheck size={16} className="text-emerald-500" /> No upfront cost</span>
              <span className="inline-flex items-center gap-2"><BadgeCheck size={16} className="text-emerald-500" /> Just 5% platform fee</span>
              <span className="inline-flex items-center gap-2"><BadgeCheck size={16} className="text-emerald-500" /> Payouts in ₹ via UPI</span>
            </div>
          </div>

          {/* Right — Live preview */}
          <div className="lg:col-span-5">
            <LivePreviewCard />
          </div>
        </div>
      </div>
    </section>
  );
}

const ACTIVITY = [
  { icon: Inbox, tone: "bg-blue-50 text-blue-700", title: "New brief: Landing page for a D2C brand", meta: "Inbound · Dubai" },
  { icon: Send, tone: "bg-indigo-50 text-indigo-700", title: "Agent sent a tailored proposal — ₹38,000", meta: "Auto-negotiated" },
  { icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-700", title: "Deal closed ✦ Client accepted", meta: "+₹38,000 to payout" },
  { icon: Wallet, tone: "bg-amber-50 text-amber-700", title: "Payout queued", meta: "₹64,500 → UPI" },
];

function LivePreviewCard() {
  // Always render all rows so the card height stays constant — only rotate which row is "latest".
  // (Previously we grew the list 1→N then reset, which oscillated page height every 1.5s and caused
  // mobile scroll jitter where the browser fought layout shifts vs. user scrolling.)
  const [latestIdx, setLatestIdx] = useState(ACTIVITY.length - 1);

  useEffect(() => {
    const t = setInterval(() => {
      setLatestIdx((v) => (v + 1) % ACTIVITY.length);
    }, 1800);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="hero-live-preview" className="relative">
      <div className="absolute -inset-4 bg-gradient-to-tr from-blue-100/60 to-sky-100/40 rounded-3xl blur-xl" />
      <div className="relative tracing-beam rounded-2xl bg-white border border-slate-200 p-5 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.35)]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-600">freelancity://agent-01</span>
          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
          </span>
        </div>

        <div className="flex items-start gap-3">
          <AgentAvatar seed="MayaCarter9" size={56} />
          <div className="flex-1 min-w-0">
            <p className="font-display text-base font-bold leading-tight">Maya Carter</p>
            <p className="text-xs text-slate-500">Senior Brand Strategist · Mumbai</p>
            <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-600">
              <Star size={11} className="fill-yellow-400 text-yellow-400" />
              <span className="font-medium text-slate-900">4.97</span>
              <span className="text-slate-400">· 312 gigs</span>
            </div>
          </div>
          <span className="text-right">
            <span className="block text-[10px] uppercase tracking-wider text-slate-400">Earned this month</span>
            <span className="block font-display font-bold text-slate-900 inline-flex items-center">
              <IndianRupee size={14} className="-mr-0.5" />2,48,500
            </span>
            <span className="block text-[10px] text-emerald-600 font-medium">↑ 18.4%</span>
          </span>
        </div>

        {/* Activity feed — height-stable: always renders all rows, only the "latest" highlight rotates */}
        <div className="mt-5 space-y-2" data-testid="hero-activity-feed">
          {ACTIVITY.map((a, i) => (
            <ActivityRow key={a.title} a={a} isLast={i === latestIdx} />
          ))}
        </div>

        {/* Footer mini-stats */}
        <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 pt-4">
          <MiniStat label="Gigs · 30d" value="11" />
          <MiniStat label="Reply" value="2m" />
          <MiniStat label="Payout" value="UPI" />
        </div>
      </div>
    </div>
  );
}

function ActivityRow({ a, isLast }) {
  return (
    <div
      data-latest={isLast}
      className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 transition-colors duration-500 ${
        isLast
          ? "border-blue-200 bg-blue-50/60"
          : "border-slate-100 bg-white"
      }`}
    >
      <span className={`mt-0.5 grid place-items-center w-7 h-7 rounded-full ${a.tone}`}>
        <a.icon size={14} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">{a.title}</p>
        <p className="text-[11px] text-slate-500">{a.meta}</p>
      </div>
      {isLast && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse mt-2.5" />}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className="font-display font-bold text-slate-900 text-sm">{value}</p>
    </div>
  );
}
